import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Progress } from "../ui/progress";
import {
  Building2, Package, Factory, ShoppingCart, Landmark,
  BarChart3, ChevronRight, ChevronLeft, X, Rocket,
  GraduationCap, CheckCircle2, ArrowRight, Settings,
} from "lucide-react";

const STEPS = [
  {
    id: "welcome",
    title: "Welcome to SteelOne ERP",
    subtitle: "Your complete manufacturing management system",
    description: "This quick setup wizard will walk you through the key features of the system. It takes about 2 minutes and you can skip anytime.",
    icon: Rocket,
    color: "from-primary/20 to-primary/5",
    iconColor: "text-primary",
  },
  {
    id: "company",
    title: "Company Profile",
    subtitle: "Your business identity",
    description: "Start by setting up your company profile — name, GST number, bank details, and logo. This information appears on all printed documents like Invoices, POs, and Quotations.",
    icon: Building2,
    color: "from-blue-500/15 to-blue-500/5",
    iconColor: "text-blue-600",
    action: { label: "Set Up Company", path: "/company" },
    tips: [
      "Add your GST number for tax-compliant documents",
      "Upload your logo to appear on all PDFs",
      "Bank details are shown on invoices for payments",
    ],
  },
  {
    id: "purchase",
    title: "Purchase Module",
    subtitle: "Manage suppliers & raw materials",
    description: "Create Purchase Orders for raw materials (billets, plates, spares). Track deliveries with GRN (Goods Receipt Notes). The system auto-generates PO numbers.",
    icon: Package,
    color: "from-amber-500/15 to-amber-500/5",
    iconColor: "text-amber-600",
    action: { label: "Go to Purchase", path: "/purchase" },
    tips: [
      "Create PO → Approve → Receive (GRN) → QC Check",
      "Download or email PO PDFs directly to suppliers",
      "Track received vs. ordered quantities automatically",
    ],
  },
  {
    id: "production",
    title: "Production Module",
    subtitle: "Work orders, entries & bundles",
    description: "Create Work Orders to assign work to machines. Log production entries per shift. Bundle finished pipes for dispatch tracking.",
    icon: Factory,
    color: "from-purple-500/15 to-purple-500/5",
    iconColor: "text-purple-600",
    action: { label: "Go to Production", path: "/production" },
    tips: [
      "Each Work Order tracks target vs. actual production",
      "Scrap and yield percentage are auto-calculated",
      "Bundles group pipes for easy dispatch management",
    ],
  },
  {
    id: "sales",
    title: "Sales Module",
    subtitle: "Quotations, orders & dispatch",
    description: "Create Quotations → convert to Sales Orders → dispatch material. Generate Proforma Invoices for advance payments. Full document lifecycle management.",
    icon: ShoppingCart,
    color: "from-emerald-500/15 to-emerald-500/5",
    iconColor: "text-emerald-600",
    action: { label: "Go to Sales", path: "/sales" },
    tips: [
      "Quotation → Sales Order → Production → Dispatch → Invoice",
      "Set tolerance % for delivery flexibility",
      "Track order-wise dispatch with Delivery Challans",
    ],
  },
  {
    id: "finance",
    title: "Finance & Invoicing",
    subtitle: "GST invoices, payments & reports",
    description: "Generate GST-compliant Tax Invoices with auto-calculated CGST/SGST/IGST. Record payments, track outstanding amounts, and download professional PDFs.",
    icon: Landmark,
    color: "from-red-500/15 to-red-500/5",
    iconColor: "text-red-600",
    action: { label: "Go to Finance", path: "/finance" },
    tips: [
      "HSN-wise tax summary on every invoice",
      "Landscape PDF format for professional printing",
      "Amount in words auto-generated",
    ],
  },
  {
    id: "complete",
    title: "You're All Set!",
    subtitle: "Start using SteelOne ERP",
    description: "You can revisit this guide anytime from the Training Guide section in the sidebar. Need help on any page? Click the help button in the bottom-right corner.",
    icon: CheckCircle2,
    color: "from-emerald-500/15 to-emerald-500/5",
    iconColor: "text-emerald-600",
    links: [
      { label: "Open Training Guide", path: "/training", icon: GraduationCap },
      { label: "Configure Settings", path: "/settings", icon: Settings },
      { label: "View Reports", path: "/reports", icon: BarChart3 },
    ],
  },
];

export default function OnboardingWizard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [setupDone, setSetupDone] = useState(false);
  const [enabledModules, setEnabledModules] = useState(null);

  const checkOnboarding = useCallback(async () => {
    try {
      const [res, setupRes, modsRes] = await Promise.all([
        api.get("/training/onboarding-status"),
        api.get("/setup/status").catch(() => ({ data: {} })),
        api.get("/modules").catch(() => ({ data: null })),
      ]);
      setSetupDone(!!setupRes.data.completed);
      if (Array.isArray(modsRes.data)) {
        const map = {};
        modsRes.data.forEach(m => { map[m.key] = m.enabled; });
        setEnabledModules(map);
      }
      if (res.data.completed || res.data.dismissed) {
        setVisible(false);
      } else {
        setStep(res.data.step || 0);
        setVisible(true);
      }
    } catch {
      // If API fails, check localStorage fallback
      const dismissed = localStorage.getItem("stancor_onboarding_dismissed");
      if (!dismissed) setVisible(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) checkOnboarding();
    else setLoading(false);
  }, [user, checkOnboarding]);

  const saveProgress = async (newStep, completed = false, dismissed = false) => {
    try {
      await api.post("/training/onboarding-progress", {
        step: newStep,
        completed,
        dismissed,
      });
    } catch {
      // Silently fail, localStorage handles fallback
    }
  };

  const handleNext = () => {
    const next = Math.min(step + 1, activeSteps.length - 1);
    setStep(next);
    saveProgress(next);
  };

  const handlePrev = () => {
    const prev = Math.max(step - 1, 0);
    setStep(prev);
    saveProgress(prev);
  };

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem("stancor_onboarding_dismissed", "true");
    saveProgress(step, false, true);
  };

  const handleComplete = () => {
    setVisible(false);
    localStorage.setItem("stancor_onboarding_dismissed", "true");
    saveProgress(activeSteps.length - 1, true, false);
  };

  const handleAction = (path) => {
    setVisible(false);
    localStorage.setItem("stancor_onboarding_dismissed", "true");
    saveProgress(step, true, false);
    navigate(path);
  };

  if (loading || !visible) return null;

  // Company profile was already collected by the Setup Wizard — don't ask again.
  // Module steps only show when that module is actually enabled (e.g. no Production for trading).
  const MODULE_STEP_IDS = ["purchase", "production", "sales", "finance"];
  const activeSteps = STEPS.filter(s => {
    if (setupDone && s.id === "company") return false;
    if (enabledModules && MODULE_STEP_IDS.includes(s.id) && !enabledModules[s.id]) return false;
    return true;
  });
  const current = activeSteps[Math.min(step, activeSteps.length - 1)];
  const Icon = current.icon;
  const progress = ((step + 1) / activeSteps.length) * 100;
  const isLast = step === activeSteps.length - 1;
  const isFirst = step === 0;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="onboarding-wizard">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleDismiss} />

      {/* Wizard Card */}
      <div className="relative w-full max-w-xl mx-4 bg-card rounded-md shadow-2xl border overflow-hidden animate-in fade-in zoom-in-95 duration-300" data-testid="onboarding-card">
        {/* Top progress */}
        <div className="px-6 pt-5 pb-0">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-muted-foreground">
              Step {step + 1} of {activeSteps.length}
            </span>
            <button
              onClick={handleDismiss}
              className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-muted transition-colors"
              data-testid="onboarding-dismiss-btn"
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
          <Progress value={progress} className="h-1.5 rounded-full" data-testid="onboarding-progress" />
        </div>

        {/* Content */}
        <div className="px-6 pt-6 pb-4">
          {/* Icon + gradient bg */}
          <div className={`w-16 h-16 rounded-md bg-gradient-to-br ${current.color} flex items-center justify-center mb-5`}>
            <Icon className={`w-8 h-8 ${current.iconColor}`} strokeWidth={1.5} />
          </div>

          <h2 className="text-xl font-bold tracking-tight" data-testid="onboarding-title">{current.title}</h2>
          <p className="text-sm font-medium text-primary mt-0.5">{current.subtitle}</p>
          <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{current.description}</p>

          {/* Tips */}
          {current.tips && (
            <div className="mt-4 space-y-2">
              {current.tips.map((tip, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm">
                  <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle2 className="w-3 h-3 text-primary" />
                  </div>
                  <span className="text-muted-foreground">{tip}</span>
                </div>
              ))}
            </div>
          )}

          {/* Action button for module steps */}
          {current.action && (
            <Button
              variant="outline"
              size="sm"
              className="mt-4 rounded-md gap-1.5"
              onClick={() => handleAction(current.action.path)}
              data-testid="onboarding-action-btn"
            >
              {current.action.label}
              <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          )}

          {/* Completion links */}
          {current.links && (
            <div className="mt-5 grid grid-cols-1 gap-2">
              {current.links.map((link) => {
                const LinkIcon = link.icon;
                return (
                  <button
                    key={link.path}
                    onClick={() => handleAction(link.path)}
                    className="flex items-center gap-3 p-3 rounded-md border hover:bg-muted/50 transition-colors text-left group"
                    data-testid={`onboarding-link-${link.path.replace("/", "")}`}
                  >
                    <LinkIcon className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium flex-1">{link.label}</span>
                    <ArrowRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="text-muted-foreground text-xs"
            data-testid="onboarding-skip-btn"
          >
            Skip tour
          </Button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrev}
                className="rounded-md gap-1"
                data-testid="onboarding-prev-btn"
              >
                <ChevronLeft className="w-3.5 h-3.5" />
                Back
              </Button>
            )}
            {isLast ? (
              <Button
                size="sm"
                onClick={handleComplete}
                className="rounded-md gap-1"
                data-testid="onboarding-complete-btn"
              >
                Get Started
                <Rocket className="w-3.5 h-3.5" />
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={handleNext}
                className="rounded-md gap-1"
                data-testid="onboarding-next-btn"
              >
                Next
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Step indicator dots */}
        <div className="flex items-center justify-center gap-1.5 pb-4">
          {activeSteps.map((_, i) => (
            <button
              key={i}
              onClick={() => { setStep(i); saveProgress(i); }}
              className={`w-2 h-2 rounded-full transition-all ${
                i === step ? "bg-primary w-5" : i < step ? "bg-primary/40" : "bg-muted-foreground/20"
              }`}
              data-testid={`onboarding-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
